import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { useQuery } from '@tanstack/react-query'
import { API_CONFIG } from 'config/api'
import { RefetchPolicy } from 'config/RefetchPolicies'
import { useConnection } from 'hooks/useConnection'
import { useMemo } from 'react'

import { useV2Pool } from './useV2Pools'

// Types
interface TokenPriceItem {
  id: string
  symbol: string
  name: string
  decimals: number
  price: number
  totalSupply: number
  priceStrategy: string
}

type TokenPriceResponse = TokenPriceItem[]

// Backend `/tokens` caps `limit` at 100 and silently drops uncached tokens beyond it.
const TOKEN_PRICES_BATCH_SIZE = 100
const TOKEN_PRICES_MAX_CONCURRENT_BATCHES = 4
const TOKEN_PRICES_RETRY_DELAY_MS = 2_000

export const TOKEN_PRICES_QUERY_KEY = 'tokenPrices'

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason)
      return
    }
    const onAbort = () => {
      clearTimeout(timer)
      reject(signal?.reason)
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    signal?.addEventListener('abort', onAbort, { once: true })
  })

const splitTokenPriceBatches = (addresses: string[]) => {
  const batches: string[][] = []

  for (let i = 0; i < addresses.length; i += TOKEN_PRICES_BATCH_SIZE) {
    batches.push(addresses.slice(i, i + TOKEN_PRICES_BATCH_SIZE))
  }

  return batches
}

const fetchTokenPricesBatch = async (
  addresses: string[],
  chainId: number,
  signal?: AbortSignal
): Promise<TokenPriceResponse> => {
  const addressesParam = addresses.join(',')
  const response = await fetch(
    `${API_CONFIG.baseUrl}/tokens?addresses=${addressesParam}&chainId=${chainId}&limit=${TOKEN_PRICES_BATCH_SIZE}`,
    {
      signal,
    }
  )

  if (!response.ok) {
    throw new Error(response.statusText)
  }

  try {
    const data = await response.json()
    return data
  } catch (error) {
    throw new Error('Error fetching token prices')
  }
}

// Fetch token prices from backend API
const fetchTokenPrices = async (
  addresses: string[],
  chainId: number,
  signal?: AbortSignal
): Promise<TokenPriceResponse> => {
  const prices: TokenPriceResponse = []
  const batches = splitTokenPriceBatches(addresses)

  for (let i = 0; i < batches.length; i += TOKEN_PRICES_MAX_CONCURRENT_BATCHES) {
    const batchResults = await Promise.allSettled(
      batches
        .slice(i, i + TOKEN_PRICES_MAX_CONCURRENT_BATCHES)
        .map((batch) => fetchTokenPricesBatch(batch, chainId, signal))
    )

    if (signal?.aborted) throw signal.reason

    const failed: string[][] = []
    batchResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        prices.push(...result.value)
      } else {
        failed.push(batches[i + index])
      }
    })
    if (failed.length > 0) {
      // Retrying only the failed batches, after a pause, keeps a rate-limited
      // response from turning into a burst of full re-fetches. A partial result
      // would be cached as complete, so a second failure fails the query instead,
      // keeping the previous prices on screen.
      await sleep(TOKEN_PRICES_RETRY_DELAY_MS, signal)
      const retried = await Promise.all(failed.map((batch) => fetchTokenPricesBatch(batch, chainId, signal)))
      retried.forEach((batchPrices) => prices.push(...batchPrices))
    }
  }

  // Treat empty arrays as errors so they don't get cached
  if (prices.length === 0) {
    throw new Error('No token data available')
  }

  return prices
}

/** Query options shared by the price hooks and background prefetching, so both hit one cache entry. */
export function tokenPricesQueryOptions(tokenIds: string[], chainId: number | undefined) {
  const sortedTokenIds = tokenIds.map((id) => id.toLowerCase()).sort()
  return {
    queryKey: [TOKEN_PRICES_QUERY_KEY, chainId, sortedTokenIds.join(',')] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchTokenPrices(sortedTokenIds, chainId as number, signal),
  }
}

const useTokenPricesQuery = (
  tokenIds: string[] | null | undefined,
  options?: { skip?: boolean; enabled?: boolean; refetchInterval?: number | false; refetchPolicy?: RefetchPolicy }
) => {
  const { chainId } = useConnection()
  const queryOptions = useMemo(() => tokenPricesQueryOptions(tokenIds ?? [], chainId), [tokenIds, chainId])
  const queryPolicy = options?.refetchPolicy ?? {}

  const query = useQuery({
    ...queryOptions,
    ...queryPolicy,
    enabled:
      !options?.skip &&
      (options?.enabled ?? true) &&
      Boolean(tokenIds?.length) &&
      (options?.refetchPolicy?.enabled ?? true),
    refetchInterval: options?.refetchInterval ?? options?.refetchPolicy?.refetchInterval ?? false,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

export const useGetTokenPrice = (
  token: string | null | undefined,
  options?: { enabled?: boolean; refetchInterval?: number | false; refetchPolicy?: RefetchPolicy }
) => {
  const tokenId = useMemo(() => token?.toLowerCase(), [token])

  const {
    data: tokenPricesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useTokenPricesQuery(tokenId ? [tokenId] : [], {
    skip: !tokenId || options?.enabled === false,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    refetchPolicy: options?.refetchPolicy,
  })

  const result = useMemo(() => {
    if (!tokenId) return null
    if (isLoading) return null
    if (!tokenPricesData) return null

    const tokenData = tokenPricesData.find((t) => t?.id?.toLowerCase() === tokenId.toLowerCase())
    return tokenData?.price ?? null
  }, [tokenId, tokenPricesData, isLoading])

  return {
    price: result,
    isLoading,
    isError,
    error,
    retry: refetch,
  }
}

export const useGetTokenPrices = (
  tokens?: (string | null | undefined)[],
  options?: { enabled?: boolean; refetchInterval?: number | false; refetchPolicy?: RefetchPolicy }
) => {
  const tokenIds = useMemo(
    () => tokens?.filter((token): token is string => !!token).map((token) => token.toLowerCase()),
    [tokens]
  )

  const {
    data: tokenPricesData,
    isLoading,
    isError,
    error,
    refetch,
  } = useTokenPricesQuery(tokenIds || [], {
    skip: !tokenIds?.length,
    enabled: options?.enabled,
    refetchInterval: options?.refetchInterval,
    refetchPolicy: options?.refetchPolicy,
  })

  const result = useMemo(() => {
    if (!tokenIds?.length) return null
    if (isLoading) return null
    if (!tokenPricesData || !Array.isArray(tokenPricesData)) return null

    return tokenIds.map((tokenId) => {
      const tokenData = tokenPricesData.find((t) => t?.id?.toLowerCase() === tokenId.toLowerCase())
      return tokenData?.price ?? null
    })
  }, [tokenIds, tokenPricesData, isLoading])

  return {
    prices: result,
    isLoading,
    isError,
    error,
    retry: refetch,
  }
}

export const useApiTokenValue = (
  amount: CurrencyAmount<Token> | null | undefined
): { value: number | null; isLoading: boolean; isError: boolean } => {
  const tokenAddress = useMemo(() => {
    if (!amount?.currency.address) return undefined
    return amount.currency.address
  }, [amount?.currency.address])

  const { price, isLoading: isPriceLoading, isError: isPriceError } = useGetTokenPrice(tokenAddress)

  const exactAmount = useMemo(() => {
    try {
      return amount?.toExact() || '0'
    } catch (error) {
      console.error('Error converting amount to exact:', error)
      return '0'
    }
  }, [amount])

  const value = useMemo(() => {
    if (!amount) return null
    if (isPriceLoading) return null
    if (isPriceError || price === null) return null

    const parsedAmount = parseFloat(exactAmount)

    if (isNaN(parsedAmount) || isNaN(price)) return null
    if (price === 0 || parsedAmount === 0) return 0

    try {
      return parsedAmount * price
    } catch (error) {
      console.error('Error calculating token value:', error)
      return null
    }
  }, [amount, exactAmount, price, isPriceLoading, isPriceError])

  return {
    value,
    isLoading: isPriceLoading,
    isError: isPriceError,
  }
}

export const useApiTokenValues = (
  amounts: CurrencyAmount<Token>[] | null | undefined
): {
  values: CurrencyAmount<Token>[] | null
  isLoading: boolean
  isError: boolean
  error: any
  retry: () => void
} => {
  const tokenAddresses = useMemo(() => {
    if (!amounts) return null
    return amounts.map((amount) => amount?.currency.address)
  }, [amounts])

  const { prices, isLoading, isError, error, retry } = useGetTokenPrices(tokenAddresses ?? [])

  const exactAmounts = useMemo(() => {
    if (!amounts) return null
    return amounts.map((amount) => {
      try {
        return amount?.toExact() || '0'
      } catch (error) {
        console.error('Error converting amount to exact:', error)
        return '0'
      }
    })
  }, [amounts])

  const values = useMemo(() => {
    if (!amounts) return null
    if (isLoading) return null
    if (!prices || !exactAmounts) return null
    if (amounts.length !== prices.length || amounts.length !== exactAmounts.length) return null

    try {
      const validTokenValues = amounts
        .map((amount, index) => {
          if (!amount || prices![index] === null) return null

          const parsedAmount = parseFloat(exactAmounts[index])
          const price = prices![index]

          if (!price || isNaN(parsedAmount) || isNaN(price)) return null

          const value = parsedAmount * (price || 0)
          if (value === 0) return null

          try {
            return CurrencyAmount.fromRawAmount(
              amount.currency,
              Math.floor(value * Math.pow(10, amount.currency.decimals))
            )
          } catch (error) {
            console.error('Error creating CurrencyAmount:', error)
            return null
          }
        })
        .filter((amount): amount is CurrencyAmount<Token> => amount !== null)

      return validTokenValues.length > 0 ? validTokenValues : null
    } catch (error) {
      console.error('Error calculating token values:', error)
      return null
    }
  }, [amounts, prices, exactAmounts, isLoading])

  return {
    values,
    isLoading,
    isError,
    error,
    retry,
  }
}

export const useApiTokenValuesWithSameLength = (
  amounts: CurrencyAmount<Token>[] | null | undefined
): {
  values: (CurrencyAmount<Token> | null)[] | null
  isLoading: boolean
  isError: boolean
  error: any
  retry: () => void
} => {
  const tokenAddresses = useMemo(() => {
    if (!amounts) return null
    return amounts.map((amount) => amount?.currency.address)
  }, [amounts])

  const { prices, isLoading, isError, error, retry } = useGetTokenPrices(tokenAddresses ?? [])

  const exactAmounts = useMemo(() => {
    if (!amounts) return null
    return amounts.map((amount) => {
      try {
        return amount?.toExact() || '0'
      } catch (error) {
        console.error('Error converting amount to exact:', error)
        return '0'
      }
    })
  }, [amounts])

  const values = useMemo(() => {
    if (!amounts) return null
    if (isLoading) return null // Still loading
    if (!prices || !exactAmounts) return null
    if (amounts.length !== prices.length || amounts.length !== exactAmounts.length) {
      console.error(
        `Array length mismatch: amounts=${amounts.length}, prices=${prices.length}, exactAmounts=${exactAmounts.length}`
      )
      return null
    }

    try {
      // Create array with same length as amounts, preserving positions with nulls
      const tokenValues = amounts.map((amount, index) => {
        if (!amount || prices![index] === null) return null

        const parsedAmount = parseFloat(exactAmounts[index])
        const price = prices![index]

        if (!price || isNaN(parsedAmount) || isNaN(price)) return null

        const value = parsedAmount * (price || 0)
        if (value === 0) return null

        try {
          return CurrencyAmount.fromRawAmount(
            amount.currency,
            Math.floor(value * Math.pow(10, amount.currency.decimals))
          )
        } catch (error) {
          console.error('Error creating CurrencyAmount:', error)
          return null
        }
      })

      // Return the array with nulls preserved, only return null if completely empty
      return tokenValues.length > 0 ? tokenValues : null
    } catch (error) {
      console.error('Error calculating token values:', error)
      return null
    }
  }, [amounts, prices, exactAmounts, isLoading])

  return {
    values,
    isLoading,
    isError,
    error,
    retry,
  }
}

export const useTokenValue = (amount: CurrencyAmount<Token> | null | undefined) => {
  const { value: apiValue } = useApiTokenValue(amount as CurrencyAmount<Token> | null | undefined)
  const poolId = amount?.currency.address

  const { pool } = useV2Pool(poolId?.toLowerCase())

  const v2LpValue = useMemo(() => {
    if (!amount || !pool || !poolId) return null
    const isV2LpToken = 'reserve0' in pool && amount.currency.isToken

    if (!isV2LpToken) return null

    try {
      const totalSupply = pool.totalSupply

      if (!totalSupply || totalSupply === 0) return null

      const userAmount = parseFloat(amount.toExact())

      const userPercentage = userAmount / totalSupply

      const poolValueUSD = parseFloat(pool.reserveUSD || '0')
      if (poolValueUSD === 0) return null

      const userValueUSD = userPercentage * poolValueUSD

      return userValueUSD
    } catch (error) {
      console.error('Error calculating V2 LP token value:', error)
      return null
    }
  }, [amount, pool, poolId])

  return useMemo(() => {
    if (v2LpValue !== null) return v2LpValue
    return apiValue
  }, [v2LpValue, apiValue])
}
